import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import { Select } from "../../Select";
import { DateInput } from "../../DateInput";
import { FormErrorContainer } from "../../FormErrorContainer";
import {
  ContentContainer,
  ModalTitle,
  FormContainer,
  FormColumn,
  ColumnTitle,
  FieldContainer,
  FieldLabel,
  RequiredMark,
  StyledInput,
  ButtonsContainer,
  CancelButton,
  SubmitButton,
} from "./AddUserModal.styled";
import {
  usersService,
  UserRole,
  CreateUserParams,
  Specialty,
} from "../../../services/usersService";
import PhoneInput from "../../PhoneInput";
import { validateUSPhoneNumber, cleanPhoneNumber } from "../../../utils/phoneValidation";

interface AddUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  dentalLicenseNumber: string;
  specialtyLicenseNumber: string;
  deaNumber: string;
  npiNumber: string;
  roleId: number | "";
  specialtyIds: number[];
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  dentalLicenseNumber?: string;
  specialtyLicenseNumber?: string;
  deaNumber?: string;
  npiNumber?: string;
  roleId?: string;
}

interface AddUserModalContentProps {
  onSuccess: () => void;
  onClose: () => void;
}

const ROLE_OPTIONS = [
  { value: UserRole.Provider, label: "Provider" },
  { value: UserRole.Assistant, label: "Assistant" },
  { value: UserRole.Manager, label: "Manager" },
];

const AddUserModalContent: React.FC<AddUserModalContentProps> = ({
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AddUserFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    dentalLicenseNumber: "",
    specialtyLicenseNumber: "",
    deaNumber: "",
    npiNumber: "",
    roleId: "",
    specialtyIds: [],
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);

  // Fetch specialties from API
  useEffect(() => {
    usersService.getSpecialties().then(setSpecialties).catch(console.error);
  }, []);

  // Validation helpers
  const validateName = (name: string): boolean => {
    // Allow alphabetic characters, hyphens, apostrophes
    const nameRegex = /^[a-zA-Z\u00C0-\u024F'-]+$/;
    return nameRegex.test(name);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    return validateUSPhoneNumber(phone);
  };

  const validateLicenseNumber = (value: string): boolean => {
    if (!value) return true; // Optional
    // Allow letters, numbers, hyphens, max 10 chars
    const regex = /^[a-zA-Z0-9-]{1,10}$/;
    return regex.test(value);
  };

  const validateDEA = (value: string): boolean => {
    if (!value) return true; // Optional
    // Exactly 2 letters + 7 numbers (e.g. AB1234567)
    const regex = /^[a-zA-Z]{2}\d{7}$/;
    return regex.test(value);
  };

  const validateNPI = (value: string): boolean => {
    if (!value) return true; // Optional
    // Numbers only, max 10 chars
    const regex = /^\d{1,10}$/;
    return regex.test(value);
  };

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // First Name
    if (!formData.firstName.trim()) {
      newErrors.firstName = t(
        "addUser.errors.firstNameRequired",
        "Please enter first name",
      );
    } else if (!validateName(formData.firstName)) {
      newErrors.firstName = t(
        "addUser.errors.invalidName",
        "Please enter a valid name",
      );
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = t(
        "addUser.errors.firstNameTooLong",
        "First name can not be longer than 50 characters",
      );
    }

    // Last Name
    if (!formData.lastName.trim()) {
      newErrors.lastName = t(
        "addUser.errors.lastNameRequired",
        "Please enter last name",
      );
    } else if (!validateName(formData.lastName)) {
      newErrors.lastName = t(
        "addUser.errors.invalidLastName",
        "Please enter a valid name, using only letters",
      );
    } else if (formData.lastName.length > 50) {
      newErrors.lastName = t(
        "addUser.errors.lastNameTooLong",
        "Last name can not be longer than 50 characters",
      );
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = t("addUser.errors.emailRequired", "Email is required");
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t(
        "addUser.errors.invalidEmail",
        "Enter a valid email address",
      );
    }

    // Phone (optional but validate format)
    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = t(
        "addUser.errors.invalidPhone",
        "Please enter a valid US phone number",
      );
    }

    // Dental License Number
    if (
      formData.dentalLicenseNumber &&
      !validateLicenseNumber(formData.dentalLicenseNumber)
    ) {
      newErrors.dentalLicenseNumber = t(
        "addUser.errors.invalidLicense",
        "Maximum 10 characters (letters, numbers, hyphens)",
      );
    }

    // Specialty License Number
    if (
      formData.specialtyLicenseNumber &&
      !validateLicenseNumber(formData.specialtyLicenseNumber)
    ) {
      newErrors.specialtyLicenseNumber = t(
        "addUser.errors.invalidLicense",
        "Maximum 10 characters (letters, numbers, hyphens)",
      );
    }

    // DEA Number
    if (formData.deaNumber && !validateDEA(formData.deaNumber)) {
      newErrors.deaNumber = t(
        "addUser.errors.invalidDEA",
        "Must be 2 letters followed by 7 numbers (e.g. AB1234567)",
      );
    }

    // NPI Number
    if (formData.npiNumber && !validateNPI(formData.npiNumber)) {
      newErrors.npiNumber = t(
        "addUser.errors.invalidNPI",
        "Numbers only, maximum 10 digits",
      );
    }

    // Role
    if (!formData.roleId) {
      newErrors.roleId = t(
        "addUser.errors.roleRequired",
        "Please select a role",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleInputChange = useCallback(
    (field: keyof AddUserFormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let value = e.target.value;

        // Apply field-specific filters
        if (field === "npiNumber") {
          // Numbers only
          value = value.replace(/\D/g, "").slice(0, 10);
        } else if (field === "deaNumber") {
          // Alphanumeric and hyphens, max 9
          value = value.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 9);
        } else if (
          field === "dentalLicenseNumber" ||
          field === "specialtyLicenseNumber"
        ) {
          // Alphanumeric and hyphens, max 10
          value = value.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 10);
        }

        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field as keyof FormErrors]) {
          setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
      },
    [errors],
  );

  const handleRoleChange = useCallback(
    (value: string | number | (string | number)[]) => {
      setFormData((prev) => ({
        ...prev,
        roleId: value as number,
        specialtyIds: [],
      }));
      if (errors.roleId) {
        setErrors((prev) => ({ ...prev, roleId: undefined }));
      }
    },
    [errors.roleId],
  );

  const handleSpecialtyChange = useCallback(
    (value: string | number | (string | number)[]) => {
      const ids = Array.isArray(value)
        ? (value as number[])
        : [value as number];
      setFormData((prev) => ({ ...prev, specialtyIds: ids }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    setApiError(null);
    if (!validate()) return;

    setLoading(true);
    try {
      const params: CreateUserParams = {
        email: formData.email,
        roleId: formData.roleId as number,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber ? cleanPhoneNumber(formData.phoneNumber) : undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
        dentalLicenseNumber: formData.dentalLicenseNumber || undefined,
        specialtyLicenseNumber: formData.specialtyLicenseNumber || undefined,
        deaNumber: formData.deaNumber || undefined,
        npiNumber: formData.npiNumber || undefined,
        specialtyIds:
          formData.specialtyIds.length > 0 ? formData.specialtyIds : undefined,
      };

      await usersService.createUser(params);
      onSuccess();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { 
          status?: number; 
          data?: { 
            detail?: string;
            errors?: Record<string, string[]>;
          } 
        };
      };

      // Handle validation errors with specific field errors from errors object
      if (axiosError.response?.data?.errors) {
        const apiErrors = axiosError.response.data.errors;
        const newErrors: FormErrors = {};
        
        // Map API field names to form field names and extract all error messages
        const fieldMapping: Record<string, keyof FormErrors> = {
          Email: "email",
          PhoneNumber: "phoneNumber",
          FirstName: "firstName",
          LastName: "lastName",
          DateOfBirth: "dateOfBirth",
          DentalLicenseNumber: "dentalLicenseNumber",
          SpecialtyLicenseNumber: "specialtyLicenseNumber",
          DeaNumber: "deaNumber",
          NpiNumber: "npiNumber",
          RoleId: "roleId",
        };

        // Process all error fields
        Object.keys(apiErrors).forEach((apiFieldName) => {
          const formFieldName = fieldMapping[apiFieldName];
          if (formFieldName && apiErrors[apiFieldName].length > 0) {
            // Join all error messages for this field
            newErrors[formFieldName] = apiErrors[apiFieldName].join(" ");
          }
        });

        if (Object.keys(newErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...newErrors }));
        } else {
          // If no mapped fields found, show generic error
          setApiError(
            axiosError.response.data.detail ||
              t(
                "addUserModal.validation.createFailed",
                "Failed to create user. Please try again.",
              ),
          );
        }
      } else {
        // Fallback to generic error message
        setApiError(
          axiosError.response?.data?.detail ||
            t(
              "addUserModal.validation.createFailed",
              "Failed to create user. Please try again.",
            ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [formData, validate, onSuccess, t]);

  const isProvider = formData.roleId === UserRole.Provider;

  const specialtyOptions = useMemo(
    () =>
      specialties.map((spec) => ({
        value: spec.id,
        label: spec.name,
      })),
    [specialties],
  );

  const allErrors = useMemo(() => {
    const errorList: string[] = [];
    if (errors.firstName) errorList.push(errors.firstName);
    if (errors.lastName) errorList.push(errors.lastName);
    if (errors.email) errorList.push(errors.email);
    if (errors.phoneNumber) errorList.push(errors.phoneNumber);
    if (errors.dateOfBirth) errorList.push(errors.dateOfBirth);
    if (errors.dentalLicenseNumber) errorList.push(errors.dentalLicenseNumber);
    if (errors.specialtyLicenseNumber)
      errorList.push(errors.specialtyLicenseNumber);
    if (errors.deaNumber) errorList.push(errors.deaNumber);
    if (errors.npiNumber) errorList.push(errors.npiNumber);
    if (errors.roleId) errorList.push(errors.roleId);
    if (apiError) errorList.push(apiError);
    return errorList;
  }, [errors, apiError]);

  return (
    <ContentContainer>
      <ModalTitle>{t("addUser.title", "Add New User")}</ModalTitle>

      <FormContainer>
        {/* Personal Information Column */}
        <FormColumn>
          <ColumnTitle>
            {t("addUser.personalInfo", "Personal Information")}
          </ColumnTitle>

          <FieldContainer>
            <FieldLabel>
              {t("addUser.firstName", "First Name")}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <StyledInput
              value={formData.firstName}
              onChange={handleInputChange("firstName")}
              hasError={!!errors.firstName}
              maxLength={50}
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>
              {t("addUser.lastName", "Last Name")}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <StyledInput
              value={formData.lastName}
              onChange={handleInputChange("lastName")}
              hasError={!!errors.lastName}
              maxLength={50}
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>
              {t("addUser.email", "Email")}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <StyledInput
              type="email"
              value={formData.email}
              onChange={handleInputChange("email")}
              hasError={!!errors.email}
            />
          </FieldContainer>

          <FieldContainer>
            <PhoneInput
              name="phoneNumber"
              label={t("addUser.phoneNumber", "Phone Number")}
              value={formData.phoneNumber}
              onChange={handleInputChange("phoneNumber")}
              placeholder="+1 (___) ___-____"
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
            />
          </FieldContainer>

          <FieldContainer>
            <DateInput
              label={t("addUser.dateOfBirth", "Date of Birth")}
              value={formData.dateOfBirth}
              onChange={handleInputChange("dateOfBirth")}
              error={!!errors.dateOfBirth}
            />
          </FieldContainer>
        </FormColumn>

        {/* Professional Credentials Column */}
        <FormColumn>
          <ColumnTitle>
            {t("addUser.professionalCredentials", "Professional Credentials")}
          </ColumnTitle>

          <FieldContainer>
            <FieldLabel>
              {t("addUser.dentalLicenseNumber", "Dental License Number")}
            </FieldLabel>
            <StyledInput
              value={formData.dentalLicenseNumber}
              onChange={handleInputChange("dentalLicenseNumber")}
              hasError={!!errors.dentalLicenseNumber}
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>
              {t("addUser.specialtyLicenseNumber", "Specialty License Number")}
            </FieldLabel>
            <StyledInput
              value={formData.specialtyLicenseNumber}
              onChange={handleInputChange("specialtyLicenseNumber")}
              hasError={!!errors.specialtyLicenseNumber}
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>{t("addUser.deaNumber", "DEA number")}</FieldLabel>
            <StyledInput
              value={formData.deaNumber}
              onChange={handleInputChange("deaNumber")}
              hasError={!!errors.deaNumber}
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>{t("addUser.npiNumber", "NPI number")}</FieldLabel>
            <StyledInput
              value={formData.npiNumber}
              onChange={handleInputChange("npiNumber")}
              hasError={!!errors.npiNumber}
            />
          </FieldContainer>

          <FieldContainer>
            <Select
              label={t("addUser.role", "Role")}
              value={formData.roleId}
              onChange={handleRoleChange}
              options={ROLE_OPTIONS}
              required
              error={!!errors.roleId}
            />
          </FieldContainer>

          {isProvider && (
            <FieldContainer>
              <Select
                label={t("addUser.specialty", "Specialty")}
                placeholder={t("addUser.selectSpecialty", "Select specialty")}
                value={formData.specialtyIds}
                onChange={handleSpecialtyChange}
                options={specialtyOptions}
                multiple
                showChips
              />
            </FieldContainer>
          )}
        </FormColumn>
      </FormContainer>

      {allErrors.length > 0 && (
        <FormErrorContainer errors={allErrors} style={{ marginTop: 32 }} />
      )}

      <ButtonsContainer>
        <CancelButton onClick={onClose} disabled={loading}>
          {t("common.cancel", "Cancel")}
        </CancelButton>
        <SubmitButton onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("addUser.submit", "Add User")
          )}
        </SubmitButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default AddUserModalContent;
